/**
 * The record panel's state: which record is open, and the sub-panels stacked
 * on it.
 *
 * The open record lives in the query string as `?record=<kind>:<id>`, so a
 * reload restores it and a panel view can be shared; the tenant of a
 * tenant-scoped kind rides ahead of the id as `<kind>:<orgId>/<id>`. The
 * stack of sub-panels pushed from inside the panel is in memory only: the
 * URL holds what is on screen, and Back is gone after a reload, which is
 * what finixt-web does too.
 */
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export const RECORD_KINDS = [
  'organization',
  'account',
  'agent',
  'task',
  'staff',
  'group',
  'collection',
  'service-token',
  'partner',
  'plugin',
  'linkage',
  // A linkage template as one organization has it: its switch and its runs.
  'org-linkage',
  // A person as one organization has them: role, groups, overrides.
  'org-user',
] as const;

export type RecordKind = (typeof RECORD_KINDS)[number];

/** Kinds whose id only means something inside one tenant. */
const TENANT_KINDS: ReadonlySet<RecordKind> = new Set([
  'agent',
  'group',
  'collection',
  'org-linkage',
  'org-user',
]);

export interface RecordRef {
  kind: RecordKind;
  id: string;
  /** Present on a tenant-scoped kind. */
  orgId?: string | undefined;
}

export const RECORD_PARAM = 'record';

function isKind(value: string): value is RecordKind {
  return (RECORD_KINDS as readonly string[]).includes(value);
}

export function isTenantKind(kind: RecordKind): boolean {
  return TENANT_KINDS.has(kind);
}

/** `kind:id`, or `kind:orgId/id` for a tenant kind; both parts URL-encoded. */
export function formatRecordParam(ref: RecordRef): string {
  const id = encodeURIComponent(ref.id);
  if (isTenantKind(ref.kind) && ref.orgId) {
    return `${ref.kind}:${encodeURIComponent(ref.orgId)}/${id}`;
  }
  return `${ref.kind}:${id}`;
}

/** The inverse of `formatRecordParam`; null for anything it would not write. */
export function parseRecordParam(value: string | null): RecordRef | null {
  if (!value) return null;
  const colon = value.indexOf(':');
  if (colon <= 0) return null;
  const kind = value.slice(0, colon);
  const rest = value.slice(colon + 1);
  if (!isKind(kind) || rest.length === 0) return null;
  try {
    if (isTenantKind(kind)) {
      const slash = rest.indexOf('/');
      if (slash <= 0 || slash === rest.length - 1) return null;
      return {
        kind,
        orgId: decodeURIComponent(rest.slice(0, slash)),
        id: decodeURIComponent(rest.slice(slash + 1)),
      };
    }
    return { kind, id: decodeURIComponent(rest) };
  } catch {
    return null;
  }
}

/**
 * A panel form, named the same way a record is: `?form=<name>`, with
 * `<orgId>/` ahead of a tenant form's name and `:<id>` after it where the
 * form acts on one thing. A record and a form are never open together.
 */
export const FORM_NAMES = [
  'invite-staff',
  'issue-token',
  'new-secret-field',
  'create-partner',
  'set-secret',
  'new-agent',
] as const;

export type FormName = (typeof FORM_NAMES)[number];

const TENANT_FORMS: ReadonlySet<FormName> = new Set(['new-agent']);

export interface FormRef {
  name: FormName;
  orgId?: string | undefined;
  /** What the form acts on, such as a secret's layer and key. */
  id?: string | undefined;
}

export const FORM_PARAM = 'form';

function isFormName(value: string): value is FormName {
  return (FORM_NAMES as readonly string[]).includes(value);
}

export function isTenantForm(name: FormName): boolean {
  return TENANT_FORMS.has(name);
}

export function formatFormParam(ref: FormRef): string {
  const head = isTenantForm(ref.name) && ref.orgId ? `${encodeURIComponent(ref.orgId)}/${ref.name}` : ref.name;
  return ref.id !== undefined ? `${head}:${encodeURIComponent(ref.id)}` : head;
}

export function parseFormParam(value: string | null): FormRef | null {
  if (!value) return null;
  const colon = value.indexOf(':');
  const head = colon >= 0 ? value.slice(0, colon) : value;
  const rawId = colon >= 0 ? value.slice(colon + 1) : null;
  if (rawId === '') return null;
  const slash = head.indexOf('/');
  const name = slash >= 0 ? head.slice(slash + 1) : head;
  if (!isFormName(name)) return null;
  try {
    const orgId = slash >= 0 ? decodeURIComponent(head.slice(0, slash)) : undefined;
    if (isTenantForm(name) !== (orgId !== undefined) || orgId === '') return null;
    const id = rawId === null ? undefined : decodeURIComponent(rawId);
    return { name, ...(orgId !== undefined ? { orgId } : {}), ...(id !== undefined ? { id } : {}) };
  } catch {
    return null;
  }
}

export function sameRecord(a: RecordRef | null, b: RecordRef | null): boolean {
  if (!a || !b) return a === b;
  return a.kind === b.kind && a.id === b.id && (a.orgId ?? '') === (b.orgId ?? '');
}

/** The in-memory stack beneath the record the URL shows. Owned by the Shell. */
export interface RecordStack {
  below: readonly RecordRef[];
  setBelow: (next: readonly RecordRef[]) => void;
}

export const RecordStackContext = createContext<RecordStack>({
  below: [],
  setBelow: () => {},
});

export interface RecordPanelApi {
  /** The record the panel shows, from the URL; null when closed. */
  current: RecordRef | null;
  /** The form the panel shows, from the URL; null when none. */
  form: FormRef | null;
  /** Open a form, closing any record. */
  openForm: (ref: FormRef) => void;
  closeForm: () => void;
  /** How many records sit beneath the current one. */
  depth: number;
  /** Open a record, discarding any stack. A row click. */
  open: (ref: RecordRef) => void;
  /** Open a record on top of the current one. A related row inside the panel. */
  push: (ref: RecordRef) => void;
  /** Return to the record beneath. */
  back: () => void;
  close: () => void;
}

/**
 * The one opener. Every row, card, palette entry, and related row that opens
 * a record goes through this; the shell reads the same state to draw the
 * panel.
 */
export function useRecordPanel(): RecordPanelApi {
  const [params, setParams] = useSearchParams();
  const { below, setBelow } = useContext(RecordStackContext);
  const current = useMemo(() => parseRecordParam(params.get(RECORD_PARAM)), [params]);
  const form = useMemo(() => parseFormParam(params.get(FORM_PARAM)), [params]);

  const write = useCallback(
    (ref: RecordRef | null) => {
      const next = new URLSearchParams(params);
      next.delete(FORM_PARAM);
      if (ref) next.set(RECORD_PARAM, formatRecordParam(ref));
      else next.delete(RECORD_PARAM);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  const writeForm = useCallback(
    (ref: FormRef | null) => {
      const next = new URLSearchParams(params);
      next.delete(RECORD_PARAM);
      if (ref) next.set(FORM_PARAM, formatFormParam(ref));
      else next.delete(FORM_PARAM);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  const openForm = useCallback(
    (ref: FormRef) => {
      setBelow([]);
      writeForm(ref);
    },
    [setBelow, writeForm],
  );
  const closeForm = useCallback(() => writeForm(null), [writeForm]);

  const open = useCallback(
    (ref: RecordRef) => {
      setBelow([]);
      write(ref);
    },
    [setBelow, write],
  );
  const push = useCallback(
    (ref: RecordRef) => {
      if (current && !sameRecord(current, ref)) setBelow([...below, current]);
      write(ref);
    },
    [below, current, setBelow, write],
  );
  const back = useCallback(() => {
    const previous = below[below.length - 1] ?? null;
    setBelow(below.slice(0, -1));
    write(previous);
  }, [below, setBelow, write]);
  const close = useCallback(() => {
    setBelow([]);
    write(null);
  }, [setBelow, write]);

  return { current, form, depth: below.length, open, push, back, close, openForm, closeForm };
}
